import { useEffect, useState } from "react";
import { Calendar, MapPin, ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import useSEO from "../hooks/useSEO";

export default function Agenda() {
  useSEO({
    title: "Agenda & Réalisations",
    description: "Nos prochains rendez-vous et les événements récemment organisés par DemkaBiz au service de la communauté francophone.",
  });
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const upcoming = events.filter((e) => e.status === "upcoming");
  const past = events.filter((e) => e.status === "past");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20" data-testid="agenda-page">
      <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-2">Médiathèque</p>
      <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-foreground tracking-tight leading-[1.05]">
        Agenda & <span className="text-primary">Réalisations</span>
      </h1>
      <p className="text-muted-foreground mt-4 max-w-2xl leading-relaxed">
        Nos prochains rendez-vous et un aperçu des événements récemment organisés ou soutenus par DemkaBiz au service de la communauté francophone.
      </p>

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <section className="mt-14">
          <div className="flex items-center gap-3 mb-6">
            <Calendar className="w-6 h-6 text-primary" />
            <h2 className="font-display font-bold text-2xl sm:text-3xl">À venir</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {upcoming.map((e) => (
              <Link
                key={e.id}
                to={`/agenda/${e.slug}`}
                className="card-surface flex flex-col overflow-hidden group hover:shadow-lg transition-all"
                data-testid={`event-upcoming-${e.slug}`}
              >
                {e.poster_url && (
                  <div className="aspect-[3/4] bg-secondary overflow-hidden flex items-center justify-center">
                    <img
                      src={e.poster_url}
                      alt={`Affiche officielle — ${e.acronym || e.title}`}
                      className="w-full h-full object-contain group-hover:scale-[1.03] transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-6 flex flex-col flex-1">
                  <span className="inline-flex self-start items-center px-2.5 py-1 rounded-full bg-accent/10 text-accent text-[11px] uppercase tracking-widest font-semibold">
                    {e.tag}
                  </span>
                  <h3 className="font-display font-semibold text-lg mt-3 leading-snug group-hover:text-primary transition-colors">{e.title}</h3>
                  {e.acronym && <p className="text-xs uppercase tracking-wider text-primary font-semibold mt-1">{e.acronym}</p>}
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed flex-1">{e.description}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mt-4 pt-4 border-t border-primary/5">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {e.date_label}</span>
                    {e.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.city}</span>}
                  </div>
                  <div className="inline-flex items-center gap-1 text-primary text-sm font-medium mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    En savoir plus <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Past */}
      {past.length > 0 && (
        <section className="mt-16">
          <div className="flex items-center gap-3 mb-6">
            <CheckCircle2 className="w-6 h-6 text-primary" />
            <h2 className="font-display font-bold text-2xl sm:text-3xl">Réalisations récentes</h2>
          </div>
          <div className="space-y-4">
            {past.map((e) => (
              <Link
                key={e.id}
                to={`/agenda/${e.slug}`}
                className="card-surface overflow-hidden flex flex-col md:flex-row group hover:shadow-lg transition-all"
                data-testid={`event-recent-${e.slug}`}
              >
                {e.poster_url && (
                  <div className="md:w-56 md:flex-shrink-0 md:aspect-square aspect-[16/9] bg-secondary overflow-hidden">
                    <img
                      src={e.poster_url}
                      alt={`Affiche — ${e.title}`}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-8 flex-1">
                  <div className="flex-1">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-primary/10 text-primary text-[11px] uppercase tracking-widest font-semibold">
                      {e.tag}
                    </span>
                    <h3 className="font-display font-semibold text-lg mt-2 leading-snug group-hover:text-primary transition-colors">{e.title}</h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{e.description}</p>
                  </div>
                  <div className="flex md:flex-col md:items-end gap-4 md:gap-1 text-xs text-muted-foreground flex-shrink-0">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {e.date_label}</span>
                    {e.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.city}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="mt-16 rounded-3xl bg-secondary/60 border border-primary/10 p-8 text-center">
        <h2 className="font-display font-bold text-2xl">Vous organisez un événement ?</h2>
        <p className="text-muted-foreground mt-2 max-w-xl mx-auto">Faites-nous signe : DemkaBiz relaie et soutient les initiatives francophones portées par la communauté.</p>
        <Link to="/contact" className="btn-primary mt-6 inline-flex" data-testid="agenda-cta-contact">
          Nous contacter <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
